import React, { useEffect, useRef, useState } from "react";
import { CommonActions, useNavigation, useRoute } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import _ from 'lodash';
import { StyleSheet, View } from "react-native";
import Text from "theme/Text";
import i18n from "utils/i18n";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import Button from "theme/Button";
import { bookingOptions } from "utils/constants/booking-constants";
import { ThemedValues } from 'styles/themed'
import FetchCachedBookingInvitationReceived from 'utils/fetch/cached/FetchCachedBookingInvitationReceived'
import { colorList } from 'utils/colors'
import IconBackground, { IconBackgroundSizes } from 'components/atoms/IconBackground'
import ThemedBlock from 'components/atoms/ThemedBlock'
import { AppRoutes } from 'navigations/app-routes'
import { TESTID } from '_shared/config/testID'
import LoadingComponent from 'components/atoms/Loading'
import { resetCart } from 'services/redux/cart/actions'
import Confetti from 'components/atoms/Confetti'
import { routeBookingConstants, routeBookingConstantValues } from 'utils/constants/route/RouteBookingConstants'
import * as StoreReview from 'expo-store-review'
import { dataConstants } from 'services/redux/data/constants'
import MutateEmployeeStoreReview from 'utils/mutate/employee-store-review'
import selectBookings from 'services/redux/data/selectors/selectBookings'
import useRouteParameters from 'utils/hooks/use-route-parameters'
import HelperDate from 'utils/helper-date'

const HomeBookFinishedScene = () => {
	
	const route = useRoute();
	const navigation = useNavigation();
	const dispatch = useDispatch()
	const _routeParameters = useRouteParameters( route );
	
	const disableTimer = _routeParameters?.disableTimer ?? false
	
	const countDownInterval = useRef();
	
	const _employee = useSelector( state => {
		return state.data[dataConstants.EMPLOYEE];
	} )
	const _bookings = useSelector( selectBookings );
	
	const bookingSize = _.get( route.params, 'bookingSize', 0 );
	const bookForOption = _.get( _routeParameters, routeBookingConstants.FOR_OPTION );
	const isMultiplePeople = bookForOption !== bookingOptions.MYSELF;
	
	const [ loading, setLoading ] = useState( true );
	const [ countdownValue, setCountdownValue ] = useState( isMultiplePeople ? 10 : 5 );
	const countDownValueRef = useRef( isMultiplePeople ? 10 : 5 );
	
	const openStoreReview = async () => {
		// Get the current date
		const now = HelperDate.date();
		
		const nextStoreReviewDatetime = _.get( _employee, 'next_store_review_datetime', null );
		if( nextStoreReviewDatetime !== null ) {
			// Get the date as a dayjs and check if that moment is after now
			const nextStoreReviewMoment = HelperDate.date( nextStoreReviewDatetime );
			
			if( now.isAfter( nextStoreReviewMoment ) ) {
				// Check if available
				if( await StoreReview.isAvailableAsync() ) {
					
					// Check if we can open the action
					if( await StoreReview.hasAction() ) {
						// Push the mutation
						MutateEmployeeStoreReview.mutate({
							callbackSuccess: () => {
								// Open the popup
								( async() => {
									try {
										await StoreReview.requestReview()
									} catch( e ) {
										// We do not have to do anything
									}
								})()
							}
						});
					}
				}
			}
		}
	}
	
	const popToTop = () => {
		let navigationRouteName = AppRoutes.TAB_BOOK;

		if( _.get( _routeParameters, routeBookingConstants.THROUGH_QR_SCAN, routeBookingConstantValues[ routeBookingConstants.THROUGH_QR_SCAN ].NO ) === routeBookingConstantValues[ routeBookingConstants.THROUGH_QR_SCAN ].YES ) {
			navigationRouteName = AppRoutes.TAB_TODAY;
		}

		dispatch( resetCart() );
		
		navigation.dispatch(
			CommonActions.reset({
				index: 0,
				routes: [
					{
						name: AppRoutes.TABS,
						params: { screen: navigationRouteName},
					},
				]
			})
		);
	}
	
	const countDown = () => {
		
		if( countDownValueRef.current <= 0 ) {
			clearInterval( countDownInterval.current );
			
			popToTop();
			return;
		}
		
		countDownValueRef.current = (countDownValueRef.current - 1)
		setCountdownValue( countDownValueRef.current )
	}
	
	useEffect( () => {
		// Trigger the store
		openStoreReview()
		
		// Init the interval
		if( disableTimer === false ) {
			countDownInterval.current = setInterval( countDown, 1000 );
		}
		
		FetchCachedBookingInvitationReceived.fetchAll()
		
		setLoading( false )
		
		return () => {
			clearInterval( countDownInterval.current );
		}
	}, [])
	
	useEffect( () => {
		if( !loading ) {
			//dispatch( resetCart() )
		}
	}, [ loading ])
	
	let content = <LoadingComponent />
	if( !loading ) {
		
		const viewHeight = 300
		
		content = (
			<View
				style={ styles.container }>
				
				<ThemedBlock
					shadow>
					
					<Confetti />
					
					<IconBackground
						colourfull={ true }
						linearGradientColours={ null }
						iconSize={ IconBackgroundSizes.S }
						opacity={ 1 }>
						
						<View
							style={ { width: '100%', height: viewHeight, alignItems: 'center', justifyContent: 'center'} }>
							
							<Text
								category={"h2"}
								style={ styles.textTextAlignCenter}>
								
								{ i18n.t('notification.booking_saved', { count: bookingSize } ) }
							</Text>
						</View>
					</IconBackground>
				</ThemedBlock>
				
				<View
					style={ [ styles.textView, styles.viewBackgroundColorPrimary ] }>
					
					<Text
						style={ styles.textTextAlignCenter }
						category={"p1"}>
						
						{ i18n.t('page.create.success.message', {
							seconds: countdownValue
						}) }
					</Text>
					
					{ (bookForOption !== null && isMultiplePeople) && (
						<Text
							style={ styles.textMarginTop12TextAlignCenter }
							category={"p1"}>
							
							{ i18n.t('page.create.success.message_invited' ) }
						</Text>
					) }
					
					<Button
						style={ styles.buttonMarginTop24 }
						onPress={ popToTop.bind( this ) }
						testID={ TESTID.HOME.BOOK.FINISHED.DONE_BUTTON }>
						
						{ i18n.t('page.create.success.button' ) }
					</Button>
				</View>
			</View>
		)
	}
	
	return (
		<ThemedSafeAreaComponentTop
			top={ true }>
			
			<ThemedLayout
				contentWidth="narrow"
				topNavigation={ false }>
				
				{ content }
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	)
}

const styles = StyleSheet.create({
	textTextAlignCenter: {
		textAlign: 'center',
	},
	viewBackgroundColorPrimary: {
		backgroundColor: colorList.PAGE.primary,
	},
	textMarginTop12TextAlignCenter: {
		marginTop: 12,
		textAlign: 'center',
	},
	buttonMarginTop24: {
		marginTop: 24,
	},
	container: {
		height: 'auto',
	},
	textView: {
		flex: 1,
		borderTopLeftRadius: 20,
		borderTopRightRadius: 20,
		padding: ThemedValues.marginHorizontal,
	}
})

export default HomeBookFinishedScene
